// The districts around the village: one place per Jane app (and a few that
// are just lovely), in the same three-quarter storybook hand as the village's
// landmarks (community_square/landmarks.js). Each returns the same parts:
//
//   sprites    upright things, painted around a base point and depth-sorted
//   decals     flat things painted into the ground layer
//   fields     farmland, painted under everything else
//   footprints rectangles/circles nobody can walk through
//   lights     warm spots that glow after dark
//   anchors    where the motion layer attaches (the windmill's sails, the
//              town hall clock, the market ticker, balloons, koi, boats)
//   walkways   piers over water that stay walkable
import { rng, inkPoly, inkRect, inkEllipse, line, roundRect, softShadow, hatch, mix, withAlpha, clipCircles, inkedCircles } from "./paint"
import { paintFlowerClump, paintBush } from "./nature"
import { lampPost, lantern, shingles, stool, bench, chair, star, cobbles, flagstones } from "./landmarks"

const HAND = '"Bradley Hand", "Segoe Print", "Chalkboard SE", "Comic Neue", cursive'

export function buildDistricts(byKey, { lake } = {}) {
    const out = { sprites: [], decals: [], fields: [], footprints: [], lights: [], anchors: {}, walkways: [] }
    const add = (part) => {
        if (!part) return
        ;["sprites", "decals", "fields", "footprints", "lights", "walkways"].forEach((k) => out[k].push(...(part[k] || [])))
        Object.assign(out.anchors, part.anchors || {})
    }
    const builders = {
        windmill, weather, library, townhall, newsstand, market, maze,
        jobs, salaries, resume, observatory,
        familytree, party, dinner, well, zen,
        harbor: (l) => harbor(l, lake), gallery, persona, postoffice
    }
    Object.entries(builders).forEach(([key, build]) => { if (byKey[key]) add(build(byKey[key])) })
    return out
}

// ---- a building, in general --------------------------------------------------
// A front wall with a door and windows, a side wall for depth, and a gable,
// hip or flat roof. The distinctive bits of each place are drawn on top.
function house(ctx, pal, o) {
    const ink = pal.ink
    const w = o.w, h = o.h, depth = o.depth ?? 30, roofH = o.roofH ?? 56
    const x0 = -w / 2, x1 = w / 2
    const wall = o.wall || pal.wall, wallShade = o.wallShade || pal.wallShade
    const roof = o.roof || pal.roof, roofDark = o.roofDark || mix(roof, "#000000", 0.22)
    // side wall
    inkPoly(ctx, [[x1, 0], [x1 + depth, -depth * 0.45], [x1 + depth, -h - depth * 0.45], [x1, -h]], wallShade, ink)
    // roof
    if (o.roofType === "flat") {
        inkPoly(ctx, [[x1, -h], [x1 + depth, -h - depth * 0.45], [x0 + depth, -h - depth * 0.45], [x0, -h]], roofDark, ink)
        inkRect(ctx, x0 - 4, -h - 8, w + 8, 10, roof, ink, { line: 1.5 })
    } else if (o.roofType === "hip") {
        const back = [[x0 + depth + 14, -h - roofH - depth * 0.45], [x1 + depth - 14, -h - roofH - depth * 0.45]]
        inkPoly(ctx, [[x1 + 10, -h + 4], [x1 + depth + 10, -h - depth * 0.45 + 4], back[1], [x1 - 14, -h - roofH]], roofDark, ink)
        const front = [[x0 - 10, -h + 4], [x1 + 10, -h + 4], [x1 - 14, -h - roofH], [x0 + 14, -h - roofH]]
        inkPoly(ctx, front, roof, ink, { line: 1.8 })
        shingles(ctx, pal, [front[3], front[2], front[1], front[0]], roof, 5)
    } else {
        const apexY = -h - roofH
        inkPoly(ctx, [[0, apexY], [depth, apexY - depth * 0.45], [x1 + depth + 12, -h - depth * 0.45 + 6], [x1 + 12, -h + 6]], roofDark, ink)
        shingles(ctx, pal, [[0, apexY], [depth, apexY - depth * 0.45], [x1 + depth + 12, -h - depth * 0.45 + 6], [x1 + 12, -h + 6]], roof, 6)
    }
    // front wall (and gable)
    const front = o.roofType === "gable" || !o.roofType
        ? [[x0, 0], [x1, 0], [x1, -h], [0, -h - roofH + 6], [x0, -h]]
        : [[x0, 0], [x1, 0], [x1, -h], [x0, -h]]
    inkPoly(ctx, front, wall, ink, { line: 1.8 })
    ctx.save()
    ctx.beginPath(); front.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip()
    hatch(ctx, [x0, -h - roofH, w, h + roofH], wallShade, { seed: w, angle: -0.3, gap: 5, alpha: 0.3 })
    if (o.bricks) for (let y = -8; y > -h; y -= 9) line(ctx, [[x0, y], [x1, y]], wallShade, 0.8, 0.8)
    ctx.restore()
    if (o.roofType === "gable" || !o.roofType) {
        const apexY = -h - roofH
        inkPoly(ctx, [[0, apexY - 4], [x0 - 16, -h + 8], [x0 - 10, -h + 16], [0, apexY + 12]], roof, ink, { line: 1.8 })
        inkPoly(ctx, [[0, apexY - 4], [x1 + 16, -h + 8], [x1 + 10, -h + 16], [0, apexY + 12]], roof, ink, { line: 1.8 })
    }
    // windows
    ;(o.windows || []).forEach(([dx, dy = -h * 0.55, ww = 24, wh = 22]) => simpleWindow(ctx, pal, dx, dy, ww, wh))
    // door
    if (o.door !== false) {
        const dw = o.doorW || 26, dh = o.doorH || Math.min(44, h * 0.62)
        ctx.beginPath()
        ctx.moveTo(-dw / 2, 0); ctx.lineTo(-dw / 2, -dh + dw / 2); ctx.arc(0, -dh + dw / 2, dw / 2, Math.PI, 0); ctx.lineTo(dw / 2, 0); ctx.closePath()
        ctx.fillStyle = o.doorColor || pal.door; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.6; ctx.stroke()
        ctx.fillStyle = "#E8C45A"; ctx.beginPath(); ctx.arc(dw * 0.28, -dh * 0.45, 1.8, 0, Math.PI * 2); ctx.fill()
        inkRect(ctx, -dw / 2 - 6, -2, dw + 12, 7, pal.stone, ink, { line: 1.2 })
    }
    if (o.sign) signBoard(ctx, pal, 0, o.signY ?? -h + 8, o.sign, o.signColor)
}

function simpleWindow(ctx, pal, cx, cy, w, h) {
    inkRect(ctx, cx - w / 2, cy - h / 2, w, h, pal.night ? pal.windowLit : pal.window, pal.ink, { line: 1.5 })
    if (!pal.night) {
        ctx.fillStyle = "rgba(255,255,255,0.5)"
        ctx.beginPath(); ctx.moveTo(cx - w / 2 + 3, cy + h / 2 - 3); ctx.lineTo(cx - w / 2 + 3, cy - h / 2 + 3); ctx.lineTo(cx - 2, cy - h / 2 + 3); ctx.closePath(); ctx.fill()
    }
    line(ctx, [[cx, cy - h / 2], [cx, cy + h / 2]], pal.timber, 1.8)
    line(ctx, [[cx - w / 2, cy], [cx + w / 2, cy]], pal.timber, 1.8)
    inkRect(ctx, cx - w / 2 - 3, cy + h / 2, w + 6, 4, pal.woodDark, pal.ink, { line: 1 })
}

function signBoard(ctx, pal, x, y, text, color) {
    ctx.font = `700 12px ${HAND}`
    const w = ctx.measureText(text).width + 16
    roundRect(ctx, x - w / 2, y - 16, w, 18, 4)
    ctx.fillStyle = color || pal.cloth; ctx.fill()
    ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.3; ctx.stroke()
    ctx.fillStyle = "#2E3431"; ctx.textAlign = "center"; ctx.textBaseline = "middle"
    ctx.fillText(text, x, y - 7)
    ctx.textBaseline = "alphabetic"
}

// The usual parts a building contributes: itself as a sprite, what blocks
// walking, and its windows as lights.
function building(l, spec, extra = {}) {
    const baseY = l.y + (spec.baseDy ?? -8)
    const w = spec.w, h = spec.h, depth = spec.depth ?? 30, roofH = spec.roofH ?? 56
    return {
        sprite: {
            x: l.x, y: baseY, box: [-w / 2 - 40, -h - roofH - (spec.tall || 0) - 40, w / 2 + depth + 40, 10],
            paint(ctx, pal) { house(ctx, pal, spec); if (spec.more) spec.more(ctx, pal) }
        },
        footprint: { x: l.x - w / 2 - 6, y: baseY - Math.min(46, h * 0.7), w: w + depth + 12, h: Math.min(46, h * 0.7) + 4 },
        lights: (spec.windows || []).map(([dx, dy = -h * 0.55]) => ({ x: l.x + dx, y: baseY + dy, r: 60 })),
        baseY,
        ...extra
    }
}

// ---- north: the windmill and its fields -----------------------------------------

function windmill(l) {
    const baseY = l.y - 6
    const towerH = 150
    const r = rng(41)
    // wheat fields in striped parcels around the mill
    const parcels = [[-250, -120, 170, 110, 0.15], [-240, 30, 150, 120, -0.1], [120, -150, 150, 110, 0.05], [130, 20, 170, 100, -0.05]]
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-70, -towerH - 60, 70, 8], kind: "built",
            paint(ctx, pal) {
                const ink = pal.ink
                // tapered tower, whitewashed, lit from the left
                const tower = [[-44, 0], [44, 0], [26, -towerH], [-26, -towerH]]
                inkPoly(ctx, tower, pal.wall, ink, { line: 1.8 })
                ctx.save(); ctx.beginPath(); tower.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip()
                ctx.fillStyle = pal.wallShade; ctx.fillRect(8, -towerH, 50, towerH)
                for (let y = -14; y > -towerH; y -= 14) line(ctx, [[-44, y], [44, y]], pal.wallShade, 0.8, 0.7)
                ctx.restore()
                // cap
                ctx.beginPath(); ctx.moveTo(-32, -towerH + 2); ctx.quadraticCurveTo(0, -towerH - 50, 32, -towerH + 2); ctx.closePath()
                ctx.fillStyle = pal.roof; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.8; ctx.stroke()
                inkRect(ctx, -34, -towerH - 2, 68, 8, pal.woodDark, ink, { line: 1.4 })
                // door, window, gallery rail
                ctx.beginPath(); ctx.moveTo(-12, 0); ctx.lineTo(-12, -24); ctx.arc(0, -24, 12, Math.PI, 0); ctx.lineTo(12, 0); ctx.closePath()
                ctx.fillStyle = pal.door; ctx.fill(); ctx.stroke()
                simpleWindow(ctx, pal, 0, -84, 16, 18)
                inkRect(ctx, -40, -54, 80, 5, pal.wood, ink, { line: 1.2 })
                for (let x = -36; x <= 36; x += 9) line(ctx, [[x, -54], [x, -64]], pal.woodDark, 1.2)
                line(ctx, [[-40, -64], [40, -64]], pal.woodDark, 1.6)
                // hub (the sails turn in the motion layer)
                inkEllipse(ctx, 0, -towerH + 8, 8, 8, pal.woodDark, ink, 1.4)
            }
        }, ...[[-150, 60], [-130, 90], [150, -40]].map(([dx, dy]) => hayBale(l.x + dx, l.y + dy))],
        fields: [(ctx, pal) => {
            parcels.forEach(([dx, dy, w, h, a], i) => {
                ctx.save()
                ctx.translate(l.x + dx + w / 2, l.y + dy + h / 2)
                ctx.rotate(a)
                ctx.fillStyle = i % 2 ? (pal.night ? "#4A4A30" : "#E8C872") : (pal.night ? "#3E4630" : "#D7C467")
                roundRect(ctx, -w / 2, -h / 2, w, h, 10); ctx.fill()
                ctx.strokeStyle = withAlpha(pal.ink, 0.35); ctx.lineWidth = 1.2; ctx.stroke()
                ctx.clip()
                for (let y = -h / 2 + 6; y < h / 2; y += 7) {
                    ctx.strokeStyle = pal.night ? "rgba(20,20,10,0.35)" : "rgba(150,110,40,0.45)"
                    ctx.lineWidth = 1.2
                    ctx.beginPath(); ctx.moveTo(-w / 2, y); ctx.lineTo(w / 2, y + (r() - 0.5) * 2); ctx.stroke()
                    for (let x = -w / 2 + 3; x < w / 2; x += 6) {
                        ctx.fillStyle = pal.night ? "rgba(120,110,60,0.5)" : "rgba(245,220,140,0.9)"
                        ctx.fillRect(x, y - 3, 1.4, 3)
                    }
                }
                ctx.restore()
            })
        }],
        footprints: [{ x: l.x - 46, y: baseY - 30, w: 92, h: 32 }],
        lights: [{ x: l.x, y: baseY - 84, r: 60 }],
        anchors: { windmill: { x: l.x, y: baseY - towerH + 8, r: 96, base: baseY } }
    }
}

function hayBale(x, y) {
    return {
        x, y, box: [-22, -30, 22, 6],
        paint(ctx, pal) {
            inkPoly(ctx, [[-18, 0], [18, 0], [18, -20], [-18, -20]], pal.night ? "#6A5E3A" : "#E3C06A", pal.ink, { line: 1.3 })
            inkEllipse(ctx, 18, -10, 7, 10, pal.night ? "#7A6E48" : "#EED28A", pal.ink, 1.2)
            for (let i = 0; i < 4; i++) line(ctx, [[-16, -4 - i * 4], [14, -3 - i * 4]], pal.night ? "#4E4428" : "#C9A04A", 0.9, 0.8)
        }
    }
}

// ---- north: the weather station --------------------------------------------------

function weather(l) {
    const baseY = l.y - 4
    return {
        sprites: [{
            x: l.x - 30, y: baseY, box: [-40, -90, 40, 6],
            paint(ctx, pal) {
                // a louvred instrument box on legs
                ;[-14, 14].forEach((x) => line(ctx, [[x, 0], [x * 0.9, -40]], pal.woodDark, 2.6))
                inkRect(ctx, -22, -74, 44, 36, pal.cloth, pal.ink, { line: 1.6 })
                for (let y = -70; y < -40; y += 5) line(ctx, [[-18, y], [18, y + 2]], pal.clothShade, 1, 0.9)
                inkPoly(ctx, [[-26, -74], [26, -74], [0, -88]], pal.cloth, pal.ink, { line: 1.4 })
            }
        }, {
            x: l.x + 36, y: baseY + 6, box: [-40, -150, 40, 6],
            paint(ctx, pal) {
                // the mast with a rooster weathervane (the cups spin in the motion layer)
                line(ctx, [[0, 0], [0, -120]], pal.ink, 3.4)
                line(ctx, [[0, 0], [0, -120]], pal.metal, 2)
                line(ctx, [[-16, -128], [16, -128]], pal.metal, 1.6)
                ctx.fillStyle = pal.night ? "#8A7A50" : "#E0A43A"
                ctx.beginPath(); ctx.moveTo(-10, -132); ctx.quadraticCurveTo(-4, -146, 4, -140); ctx.lineTo(10, -146); ctx.lineTo(8, -136); ctx.lineTo(14, -132); ctx.closePath(); ctx.fill()
                ctx.strokeStyle = pal.ink; ctx.lineWidth = 1; ctx.stroke()
                ;[["N", 0, -112], ["E", 12, -104], ["W", -12, -104]].forEach(([t, x, y]) => { ctx.fillStyle = pal.label; ctx.font = `700 9px ${HAND}`; ctx.textAlign = "center"; ctx.fillText(t, x, y) })
            }
        }, {
            x: l.x - 70, y: baseY + 20, box: [-10, -30, 10, 4],
            paint(ctx, pal) {
                // rain gauge
                line(ctx, [[0, 0], [0, -14]], pal.woodDark, 2)
                inkRect(ctx, -5, -28, 10, 16, withAlpha(pal.window, 0.8), pal.ink, { line: 1 })
                ctx.fillStyle = pal.water; ctx.fillRect(-4, -18, 8, 5)
            }
        }],
        decals: [(ctx, pal) => { flagstones(ctx, pal, l.x, l.y + 8, 78, 26, 51) }],
        footprints: [{ x: l.x - 56, y: baseY - 10, w: 50, h: 12 }, { circle: true, x: l.x + 36, y: baseY + 4, r: 6 }],
        lights: [{ x: l.x - 30, y: baseY - 56, r: 40 }],
        anchors: { anemometer: { x: l.x + 36, y: baseY + 6 - 120 } }
    }
}

// ---- north: the library -----------------------------------------------------------

function library(l) {
    const b = building(l, {
        w: 210, h: 84, depth: 34, roofType: "flat", wall: undefined, bricks: true,
        windows: [[-70, -46, 26, 34], [70, -46, 26, 34]], doorW: 30, doorH: 50,
        more(ctx, pal) {
            const ink = pal.ink
            // portico: pediment on four columns, and steps
            inkPoly(ctx, [[-66, -92], [66, -92], [0, -130]], pal.stoneLight, ink, { line: 1.8 })
            ctx.fillStyle = pal.label; ctx.font = `700 13px ${HAND}`; ctx.textAlign = "center"; ctx.fillText("LIBRARY", 0, -100)
            inkRect(ctx, -70, -92, 140, 8, pal.stone, ink, { line: 1.4 })
            ;[-54, -20, 20, 54].forEach((x) => {
                inkRect(ctx, x - 6, -84, 12, 82, pal.stoneLight, ink, { line: 1.3 })
                line(ctx, [[x - 2, -80], [x - 2, -6]], pal.stone, 1, 0.8)
                inkRect(ctx, x - 8, -86, 16, 4, pal.stone, ink, { line: 1 })
            })
            ;[0, 1, 2].forEach((i) => inkRect(ctx, -76 + i * 4, -2 + i * 6 - 4, 152 - i * 8, 6, pal.stone, ink, { line: 1.1 }))
            // books glimpsed through the windows
            ;[-70, 70].forEach((cx) => {
                for (let i = 0; i < 6; i++) {
                    ctx.fillStyle = pal.flag[i % pal.flag.length]
                    ctx.fillRect(cx - 11 + i * 3.6, -40, 2.8, 10)
                    ctx.fillRect(cx - 11 + i * 3.6, -56, 2.8, 9)
                }
            })
        }
    })
    return {
        sprites: [b.sprite, bench(l.x - 120, l.y + 30, 50), bench(l.x + 130, l.y + 30, 50)],
        decals: [(ctx, pal) => flagstones(ctx, pal, l.x, l.y + 14, 120, 30, 61)],
        footprints: [b.footprint],
        lights: b.lights
    }
}

// ---- north: the town hall ---------------------------------------------------------

function townhall(l) {
    const towerTop = -84 - 150
    const b = building(l, {
        w: 240, h: 84, depth: 36, roofType: "hip", roofH: 40, tall: 110,
        windows: [[-86, -46], [-46, -46], [46, -46], [86, -46]], doorW: 36, doorH: 52, doorColor: "#6D4A34",
        more(ctx, pal) {
            const ink = pal.ink
            // the clock tower rising from the middle of the roof
            inkRect(ctx, -30, towerTop + 44, 60, 128 - 44, pal.wall, ink, { line: 1.8 })
            ctx.save(); ctx.beginPath(); ctx.rect(-30, towerTop + 44, 60, 84); ctx.clip()
            ctx.fillStyle = pal.wallShade; ctx.fillRect(10, towerTop + 44, 24, 84)
            ctx.restore()
            inkPoly(ctx, [[-38, towerTop + 46], [38, towerTop + 46], [0, towerTop]], pal.roof, ink, { line: 1.8 })
            inkEllipse(ctx, 0, towerTop + 76, 20, 20, pal.cloth, ink, 1.8)
            for (let i = 0; i < 12; i++) {
                const a = (i / 12) * Math.PI * 2
                line(ctx, [[Math.cos(a) * 15, towerTop + 76 + Math.sin(a) * 15], [Math.cos(a) * 18, towerTop + 76 + Math.sin(a) * 18]], ink, i % 3 ? 1 : 2)
            }
            // flag on the spire
            line(ctx, [[0, towerTop], [0, towerTop - 26]], ink, 1.6)
            inkPoly(ctx, [[0, towerTop - 26], [22, towerTop - 20], [0, towerTop - 14]], pal.flag[0], ink, { line: 1 })
            signBoard(ctx, pal, 0, -60, "TOWN HALL", pal.cloth)
            ;[0, 1].forEach((i) => inkRect(ctx, -44 + i * 5, -2 + i * 6 - 4, 88 - i * 10, 6, pal.stone, ink, { line: 1.1 }))
        }
    })
    return {
        sprites: [b.sprite, lampPost(l.x - 150, l.y + 24), lampPost(l.x + 150, l.y + 24)],
        decals: [(ctx, pal) => {
            cobbles(ctx, pal, l.x, l.y + 20, 150, 40, 71)
            paintFlowerClump(ctx, pal, l.x - 110, l.y - 2, 72)
            paintFlowerClump(ctx, pal, l.x + 110, l.y - 2, 73)
        }],
        footprints: [b.footprint],
        lights: [...b.lights, { x: l.x - 150, y: l.y - 42, r: 80 }, { x: l.x + 150, y: l.y - 42, r: 80 }, { x: l.x, y: b.baseY + towerTop + 76, r: 50 }],
        anchors: { clock: { x: l.x, y: b.baseY + towerTop + 76, r: 16, base: b.baseY } }
    }
}

// ---- north: the newsstand ----------------------------------------------------------

function newsstand(l) {
    const baseY = l.y - 6
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-80, -140, 80, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                const green = pal.night ? "#2F5243" : "#3F8A66", greenDark = mix(green, "#000000", 0.25)
                // an octagonal kiosk: three visible faces
                inkPoly(ctx, [[20, 0], [48, -10], [48, -84], [20, -74]], greenDark, ink)
                inkPoly(ctx, [[-20, 0], [-48, -10], [-48, -84], [-20, -74]], greenDark, ink)
                inkRect(ctx, -20, -74, 40, 74, green, ink, { line: 1.8 })
                // the counter window with papers
                inkRect(ctx, -16, -60, 32, 26, pal.cloth, ink, { line: 1.2 })
                for (let i = 0; i < 4; i++) line(ctx, [[-12, -54 + i * 5], [12, -54 + i * 5]], pal.inkSoft, 0.8, 0.8)
                // the domed roof with a finial
                ctx.beginPath(); ctx.moveTo(-54, -82); ctx.quadraticCurveTo(0, -140, 54, -82); ctx.closePath()
                ctx.fillStyle = greenDark; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.8; ctx.stroke()
                inkRect(ctx, -56, -86, 112, 8, "#E8C45A", ink, { line: 1.3 })
                ctx.fillStyle = "#2E3431"; ctx.font = `700 7px ${HAND}`; ctx.textAlign = "center"; ctx.fillText("THE WIRE", 0, -80)
                inkEllipse(ctx, 0, -124, 5, 5, "#E8C45A", ink, 1.2)
                // racks of papers either side
                ;[-40, 34].forEach((x) => {
                    for (let i = 0; i < 3; i++) inkRect(ctx, x, -40 + i * 12, 12, 9, i % 2 ? pal.cloth : "#F4EDD8", ink, { line: 0.8 })
                })
            }
        }, {
            x: l.x + 80, y: l.y + 26, box: [-26, -34, 26, 6],
            paint(ctx, pal) {
                // a sandwich board with today's headline
                line(ctx, [[-14, 0], [0, -30], [14, 0]], pal.woodDark, 2.4)
                inkPoly(ctx, [[-12, -4], [-1, -28], [1, -28], [12, -4]], pal.cloth, pal.ink, { line: 1.2 })
                ctx.fillStyle = "#C84B4B"; ctx.font = `700 6px ${HAND}`; ctx.textAlign = "center"; ctx.fillText("EXTRA!", 0, -14)
            }
        }],
        decals: [(ctx, pal) => flagstones(ctx, pal, l.x, l.y + 12, 80, 26, 81)],
        footprints: [{ x: l.x - 50, y: baseY - 20, w: 100, h: 22 }],
        lights: [{ x: l.x, y: baseY - 50, r: 70 }]
    }
}

// ---- north: the market ---------------------------------------------------------------

function market(l) {
    const stalls = [[-92, 0, ["#C84B4B", "#FAF7EF"]], [0, -14, ["#E8B43A", "#FAF7EF"]], [92, 0, ["#3F8A66", "#FAF7EF"]]]
    const produce = [["#D2544F", "#F39A5B", "#F2C14E"], ["#8FB061", "#6E9F6B", "#B7A2E6"], ["#F2C14E", "#E8894A", "#D2544F"]]
    const sprites = stalls.map(([dx, dy, stripes], i) => ({
        x: l.x + dx, y: l.y + dy + 6, box: [-50, -96, 50, 8],
        paint(ctx, pal) {
            const ink = pal.ink
            ;[-38, 38].forEach((x) => line(ctx, [[x, 0], [x, -70]], pal.woodDark, 3))
            // counter with crates of produce
            inkRect(ctx, -40, -28, 80, 28, pal.wood, ink, { line: 1.5 })
            for (let c = 0; c < 3; c++) {
                inkRect(ctx, -36 + c * 25, -40, 22, 12, pal.woodLight, ink, { line: 1 })
                const r = rng(i * 10 + c)
                for (let k = 0; k < 6; k++) { ctx.fillStyle = produce[i][(k + c) % 3]; ctx.beginPath(); ctx.arc(-32 + c * 25 + r() * 14, -42 - r() * 4, 3.2, 0, Math.PI * 2); ctx.fill() }
            }
            // striped awning with a scalloped edge
            for (let s = 0; s < 8; s++) {
                const x = -46 + s * 11.5
                inkPoly(ctx, [[x, -84], [x + 11.5, -84], [x + 11.5 + 2, -66], [x + 2, -66]], stripes[s % 2], ink, { line: 1 })
                ctx.beginPath(); ctx.arc(x + 7.7, -66, 5.75, 0, Math.PI); ctx.fillStyle = stripes[s % 2]; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1; ctx.stroke()
            }
            line(ctx, [[-46, -84], [46, -84]], ink, 1.6)
        }
    }))
    // the ticker board (its prices scroll in the motion layer)
    const ticker = { x: l.x - 70, y: l.y - 136, w: 140, h: 26 }
    sprites.push({
        x: l.x, y: l.y - 90, box: [-84, -60, 84, 6],
        paint(ctx, pal) {
            ;[-66, 66].forEach((x) => line(ctx, [[x, 0], [x, -50]], pal.woodDark, 3.2))
            inkRect(ctx, -74, -50, 148, 30, "#1F2628", pal.ink, { line: 1.8 })
            inkRect(ctx, -74, -58, 148, 8, pal.woodDark, pal.ink, { line: 1.2 })
        }
    })
    return {
        sprites,
        decals: [(ctx, pal) => {
            cobbles(ctx, pal, l.x, l.y + 10, 150, 44, 91)
            const r = rng(92)
            for (let i = 0; i < 10; i++) { ctx.fillStyle = produce[i % 3][i % 3]; ctx.globalAlpha = 0.7; ctx.beginPath(); ctx.arc(l.x + (r() - 0.5) * 220, l.y + 30 + r() * 20, 2.4, 0, Math.PI * 2); ctx.fill() }
            ctx.globalAlpha = 1
        }],
        footprints: stalls.map(([dx, dy]) => ({ x: l.x + dx - 42, y: l.y + dy - 16, w: 84, h: 22 })).concat([{ x: l.x - 70, y: l.y - 96, w: 10, h: 8 }, { x: l.x + 60, y: l.y - 96, w: 10, h: 8 }]),
        lights: stalls.map(([dx, dy]) => ({ x: l.x + dx, y: l.y + dy - 50, r: 60 })),
        anchors: { ticker: { x: ticker.x + 4, y: l.y - 90 - 50 + 4, w: 140, h: 22, base: l.y - 90 } }
    }
}

// ---- north-east: the hedge maze ---------------------------------------------------------
// A real maze: a seeded recursive backtracker on a small grid, drawn as tall
// clipped hedges. It is scenery (too tight to walk), with a fountain at its
// heart and an arch at the entrance.

function maze(l) {
    const cols = 9, rows = 6, cell = 26
    const w = cols * cell, h = rows * cell
    const x0 = l.x - w / 2, y0 = l.y - h / 2 - 20
    const r = rng(1234)
    const walls = { h: new Set(), v: new Set() } // removed walls
    const seen = new Set(["0,0"])
    const stack = [[0, 0]]
    while (stack.length) {
        const [cx, cy] = stack[stack.length - 1]
        const next = [[1, 0], [-1, 0], [0, 1], [0, -1]].map(([dx, dy]) => [cx + dx, cy + dy, dx, dy]).filter(([x, y]) => x >= 0 && y >= 0 && x < cols && y < rows && !seen.has(`${x},${y}`))
        if (!next.length) { stack.pop(); continue }
        const [nx, ny, dx, dy] = next[Math.floor(r() * next.length)]
        if (dx) walls.v.add(`${Math.max(cx, nx)},${cy}`)
        else walls.h.add(`${cx},${Math.max(cy, ny)}`)
        seen.add(`${nx},${ny}`)
        stack.push([nx, ny])
    }
    const entrance = Math.floor(cols / 2)
    return {
        sprites: [{
            x: l.x, y: y0 + h + 4, box: [-40, -70, 40, 6],
            paint(ctx, pal) {
                // a clipped-hedge arch over the way in
                const hedge = pal.leafDark
                inkRect(ctx, -30, -46, 12, 46, hedge, pal.ink, { line: 1.4 })
                inkRect(ctx, 18, -46, 12, 46, hedge, pal.ink, { line: 1.4 })
                ctx.beginPath(); ctx.moveTo(-30, -44); ctx.quadraticCurveTo(0, -78, 30, -44); ctx.lineTo(18, -44); ctx.quadraticCurveTo(0, -64, -18, -44); ctx.closePath()
                ctx.fillStyle = hedge; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.4; ctx.stroke()
                ctx.fillStyle = pal.leaf; ctx.fillRect(-28, -44, 4, 40); ctx.fillRect(20, -44, 4, 40)
            }
        }],
        decals: [(ctx, pal) => {
            const ink = pal.ink
            // gravel floor
            ctx.fillStyle = pal.pathLight
            roundRect(ctx, x0 - 10, y0 - 10, w + 20, h + 20, 8); ctx.fill()
            // hedges: a darker base (the side you see) under a lit top
            const drawWall = (ax, ay, bx, by) => {
                ctx.strokeStyle = ink; ctx.lineWidth = 12; ctx.lineCap = "round"
                ctx.beginPath(); ctx.moveTo(ax, ay + 3); ctx.lineTo(bx, by + 3); ctx.stroke()
                ctx.strokeStyle = pal.leafShadow; ctx.lineWidth = 10
                ctx.beginPath(); ctx.moveTo(ax, ay + 3); ctx.lineTo(bx, by + 3); ctx.stroke()
                ctx.strokeStyle = pal.leaf; ctx.lineWidth = 8
                ctx.beginPath(); ctx.moveTo(ax, ay - 1); ctx.lineTo(bx, by - 1); ctx.stroke()
            }
            for (let cy = 0; cy <= rows; cy++) for (let cx = 0; cx < cols; cx++) {
                if (cy > 0 && cy < rows && walls.h.has(`${cx},${cy}`)) continue
                if (cy === rows && cx === entrance) continue
                if (cy === 0 && cx === entrance) continue
                drawWall(x0 + cx * cell, y0 + cy * cell, x0 + (cx + 1) * cell, y0 + cy * cell)
            }
            for (let cx = 0; cx <= cols; cx++) for (let cy = 0; cy < rows; cy++) {
                if (cx > 0 && cx < cols && walls.v.has(`${cx},${cy}`)) continue
                drawWall(x0 + cx * cell, y0 + cy * cell, x0 + cx * cell, y0 + (cy + 1) * cell)
            }
            // highlights on the hedge tops
            ctx.strokeStyle = withAlpha(pal.leafLight, 0.7); ctx.lineWidth = 2; ctx.lineCap = "round"
            for (let cy = 0; cy <= rows; cy += 2) { ctx.beginPath(); ctx.moveTo(x0, y0 + cy * cell - 3); ctx.lineTo(x0 + 20, y0 + cy * cell - 3); ctx.stroke() }
            // the fountain at the heart
            const fx = x0 + entrance * cell + cell / 2, fy = y0 + Math.floor(rows / 2) * cell - cell / 2
            inkEllipse(ctx, fx, fy, 10, 6, pal.stone, ink, 1.3)
            inkEllipse(ctx, fx, fy - 1, 7, 4, pal.water, null)
        }],
        footprints: [{ x: x0 - 8, y: y0 - 8, w: w + 16, h: h + 4 }],
        lights: [],
        anchors: { mazeFountain: { x: x0 + entrance * cell + cell / 2, y: y0 + Math.floor(rows / 2) * cell - cell / 2 } }
    }
}

// ---- east: the job board ------------------------------------------------------------

function jobs(l) {
    const baseY = l.y - 8
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-110, -150, 110, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                ;[-84, 84].forEach((x) => inkRect(ctx, x - 6, -118, 12, 118, pal.woodDark, ink, { line: 1.5 }))
                inkPoly(ctx, [[-100, -114], [100, -114], [86, -138], [-86, -138]], pal.roof, ink, { line: 1.8 })
                shingles(ctx, pal, [[-86, -138], [86, -138], [100, -114], [-100, -114]], pal.roof, 4)
                inkRect(ctx, -80, -112, 160, 84, pal.woodLight, ink, { line: 1.8 })
                inkRect(ctx, -74, -106, 148, 72, pal.night ? "#6E5A45" : "#C99A66", ink, { line: 1 })
                signBoard(ctx, pal, 0, -118, "JOBS BY JANE", "#F2C14E")
                // job postings, each with a title line and a colored tag
                const r = rng(501)
                for (let row = 0; row < 3; row++) for (let col = 0; col < 5; col++) {
                    const x = -68 + col * 28 + (r() - 0.5) * 3, y = -100 + row * 22 + (r() - 0.5) * 3
                    ctx.save(); ctx.translate(x + 11, y + 9); ctx.rotate((r() - 0.5) * 0.14)
                    inkRect(ctx, -11, -9, 22, 18, pal.night ? mix("#FFFFFF", "#1B2426", 0.4) : "#FFFFFF", ink, { line: 0.8 })
                    line(ctx, [[-8, -4], [7, -4]], pal.inkSoft, 1.2, 0.8)
                    line(ctx, [[-8, 0], [4, 0]], pal.inkSoft, 0.7, 0.6)
                    ctx.fillStyle = pal.flag[(row + col) % pal.flag.length]; ctx.fillRect(-8, 3, 7, 3)
                    ctx.fillStyle = "#C84B4B"; ctx.beginPath(); ctx.arc(0, -8, 1.6, 0, Math.PI * 2); ctx.fill()
                    ctx.restore()
                }
            }
        }, {
            // a "Now hiring" flag
            x: l.x + 120, y: l.y + 14, box: [-10, -100, 50, 6],
            paint(ctx, pal) {
                line(ctx, [[0, 0], [0, -92]], pal.metal, 2.4)
                inkPoly(ctx, [[0, -92], [40, -84], [0, -70]], pal.flag[1], pal.ink, { line: 1.2 })
                ctx.fillStyle = "#2E3431"; ctx.font = `700 7px ${HAND}`; ctx.textAlign = "left"; ctx.fillText("HIRING", 3, -80)
            }
        }, stool(l.x - 110, l.y + 24)],
        decals: [(ctx, pal) => flagstones(ctx, pal, l.x, l.y + 14, 110, 30, 511)],
        footprints: [{ x: l.x - 92, y: baseY - 10, w: 184, h: 12 }],
        lights: [{ x: l.x, y: baseY - 70, r: 100 }]
    }
}

// ---- east: the counting house ------------------------------------------------------------

function salaries(l) {
    const b = building(l, {
        w: 170, h: 90, depth: 32, roofType: "flat", bricks: true,
        wall: undefined, windows: [[-56, -50, 22, 30], [56, -50, 22, 30]], doorW: 30, doorH: 52, doorColor: "#3E5F4A",
        more(ctx, pal) {
            const ink = pal.ink
            ;[-30, 30].forEach((x) => { inkRect(ctx, x - 7, -90, 14, 88, pal.stoneLight, ink, { line: 1.3 }); inkRect(ctx, x - 9, -92, 18, 5, pal.stone, ink, { line: 1 }) })
            inkRect(ctx, -90, -104, 180, 14, pal.stoneLight, ink, { line: 1.6 })
            // the coin medallion
            inkEllipse(ctx, 0, -120, 17, 17, "#E8C45A", ink, 1.8)
            inkEllipse(ctx, 0, -120, 12, 12, "#F2D27A", ink, 1)
            ctx.fillStyle = "#8A6A20"; ctx.font = `700 16px ${HAND}`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("$", 0, -119)
            ctx.textBaseline = "alphabetic"
            ctx.fillStyle = pal.label; ctx.font = `700 10px ${HAND}`; ctx.fillText("COUNTING HOUSE", 0, -94)
        }
    })
    return {
        sprites: [b.sprite, {
            x: l.x + 110, y: l.y + 22, box: [-22, -36, 22, 6],
            paint(ctx, pal) {
                // sacks and stacks of coins
                ;[[-10, "#C9A56A"], [8, "#B8935A"]].forEach(([dx, c]) => {
                    ctx.beginPath(); ctx.moveTo(dx - 9, 0); ctx.quadraticCurveTo(dx - 12, -18, dx - 3, -22); ctx.lineTo(dx + 3, -22); ctx.quadraticCurveTo(dx + 12, -18, dx + 9, 0); ctx.closePath()
                    ctx.fillStyle = c; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
                    line(ctx, [[dx - 4, -20], [dx + 4, -20]], pal.ink, 1.4)
                })
                for (let i = 0; i < 4; i++) inkEllipse(ctx, -24, -2 - i * 3, 6, 2.4, "#E8C45A", pal.ink, 0.8)
            }
        }],
        decals: [(ctx, pal) => cobbles(ctx, pal, l.x, l.y + 16, 110, 30, 521)],
        footprints: [b.footprint],
        lights: b.lights
    }
}

// ---- east: the print shop --------------------------------------------------------------

function resume(l) {
    const b = building(l, {
        w: 160, h: 72, depth: 30, roofType: "gable", roofH: 50, roof: undefined,
        windows: [[46, -40, 30, 30]], doorW: 26, doorH: 46,
        more(ctx, pal) {
            const ink = pal.ink
            // the big shop window with stacks of paper
            inkRect(ctx, -70, -58, 44, 40, pal.night ? pal.windowLit : pal.window, ink, { line: 1.6 })
            for (let i = 0; i < 4; i++) inkRect(ctx, -64 + i * 9, -30 - i * 3, 8, 10 + i * 3, "#FFFFFF", ink, { line: 0.7 })
            // a hanging sign on a bracket
            line(ctx, [[80, -66], [106, -66]], ink, 2)
            line(ctx, [[98, -66], [98, -60]], ink, 1)
            roundRect(ctx, 84, -60, 30, 22, 3); ctx.fillStyle = pal.cloth; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.2; ctx.stroke()
            ctx.fillStyle = "#2E3431"; ctx.font = `700 6.5px ${HAND}`; ctx.textAlign = "center"; ctx.fillText("PRINT", 99, -51); ctx.fillText("SHOP", 99, -43)
        }
    })
    return {
        sprites: [b.sprite, {
            x: l.x - 118, y: l.y + 24, box: [-30, -40, 30, 8],
            paint(ctx, pal) {
                // a cart of paper rolls
                inkRect(ctx, -24, -18, 48, 10, pal.wood, pal.ink, { line: 1.3 })
                ;[-14, 14].forEach((x) => inkEllipse(ctx, x, -6, 6, 6, pal.woodDark, pal.ink, 1.2))
                ;[-12, 0, 12].forEach((x) => inkEllipse(ctx, x, -26, 6, 8, "#FFFFFF", pal.ink, 1.1))
                line(ctx, [[24, -14], [34, -26]], pal.woodDark, 2)
            }
        }],
        decals: [(ctx, pal) => flagstones(ctx, pal, l.x, l.y + 14, 100, 28, 531)],
        footprints: [b.footprint],
        lights: [...b.lights, { x: l.x - 48, y: b.baseY - 38, r: 60 }]
    }
}

// ---- east: the observatory ---------------------------------------------------------------

function observatory(l) {
    const baseY = l.y - 10
    return {
        sprites: [{
            x: l.x + 20, y: baseY, box: [-80, -190, 90, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                // round stone tower
                inkPoly(ctx, [[-48, 0], [48, 0], [48, -96], [-48, -96]], pal.stone, ink, { line: 1.8 })
                ctx.save(); ctx.beginPath(); ctx.rect(-48, -96, 96, 96); ctx.clip()
                ctx.fillStyle = pal.stoneDark; ctx.fillRect(18, -96, 34, 96)
                for (let y = -12; y > -96; y -= 12) line(ctx, [[-48, y], [48, y]], pal.stoneDark, 0.8, 0.8)
                ctx.restore()
                inkEllipse(ctx, 0, 0, 48, 10, null, ink, 1.4)
                // the dome, with its slit and the telescope poking out
                ctx.beginPath(); ctx.moveTo(-54, -94); ctx.bezierCurveTo(-54, -160, 54, -160, 54, -94); ctx.closePath()
                const g = ctx.createLinearGradient(-54, -150, 54, -94)
                g.addColorStop(0, pal.night ? "#8A9AA6" : "#E6EEF2"); g.addColorStop(1, pal.night ? "#4A5660" : "#A9B8C2")
                ctx.fillStyle = g; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.8; ctx.stroke()
                inkPoly(ctx, [[-6, -150], [6, -150], [8, -96], [-8, -96]], "#2B3236", ink, { line: 1.2 })
                ctx.save(); ctx.translate(0, -132); ctx.rotate(-0.55)
                inkRect(ctx, -6, -44, 12, 50, pal.metal, ink, { line: 1.4 })
                inkRect(ctx, -8, -50, 16, 8, "#E8C45A", ink, { line: 1.2 })
                ctx.restore()
                ctx.beginPath(); ctx.moveTo(-10, 0); ctx.lineTo(-10, -30); ctx.arc(0, -30, 10, Math.PI, 0); ctx.lineTo(10, 0); ctx.closePath()
                ctx.fillStyle = pal.door; ctx.fill(); ctx.stroke()
                simpleWindow(ctx, pal, -26, -60, 14, 16)
            }
        }, {
            // the globe on its stand
            x: l.x - 88, y: l.y + 20, box: [-30, -76, 30, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                line(ctx, [[-12, 0], [0, -8], [12, 0]], pal.woodDark, 2.4)
                line(ctx, [[0, -8], [0, -26]], pal.woodDark, 3)
                inkEllipse(ctx, 0, -48, 22, 22, pal.night ? "#2E5A6A" : "#6FB0CF", ink, 1.6)
                ctx.save(); ctx.beginPath(); ctx.arc(0, -48, 22, 0, Math.PI * 2); ctx.clip()
                ctx.fillStyle = pal.night ? "#3E6A48" : "#8FC07A"
                ;[[-8, -56, 9, 6], [8, -44, 8, 10], [-4, -36, 6, 4], [12, -60, 5, 4]].forEach(([x, y, rx, ry]) => { ctx.beginPath(); ctx.ellipse(x, y, rx, ry, 0.4, 0, Math.PI * 2); ctx.fill() })
                ctx.restore()
                ctx.strokeStyle = "#E8C45A"; ctx.lineWidth = 2
                ctx.beginPath(); ctx.arc(0, -48, 26, Math.PI * 0.6, Math.PI * 2.1); ctx.stroke()
            }
        }],
        decals: [(ctx, pal) => cobbles(ctx, pal, l.x, l.y + 14, 120, 34, 541)],
        footprints: [{ circle: true, x: l.x + 20, y: baseY - 10, r: 48 }, { circle: true, x: l.x - 88, y: l.y + 18, r: 10 }],
        lights: [{ x: l.x + 20, y: baseY - 60, r: 60 }]
    }
}

// ---- south: the family tree --------------------------------------------------------------
// One enormous old oak with a swing, lanterns in its branches and little
// name plaques on its roots — every branch a generation.

function familytree(l) {
    const s = 64
    const baseY = l.y - 20
    const lanterns = [[-70, -150], [60, -170], [-20, -200], [90, -120], [-100, -110]]
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-160, -250, 160, 20], sway: 0.25,
            paint(ctx, pal) {
                const ink = pal.ink
                // roots and a massive trunk
                inkPoly(ctx, [[-60, 6], [-30, -10], [-26, -80], [-14, -120], [14, -120], [26, -80], [30, -10], [64, 8], [20, 0], [0, 10], [-22, 0]], pal.trunk, ink, { line: 2 })
                for (let i = 0; i < 6; i++) line(ctx, [[-16 + i * 7, -8], [-12 + i * 5, -110]], pal.trunkDark, 1.2, 0.7)
                // branches reaching out to the canopy
                line(ctx, [[-10, -100], [-70, -150]], ink, 13); line(ctx, [[-10, -100], [-70, -150]], pal.trunk, 9)
                line(ctx, [[10, -104], [80, -150]], ink, 13); line(ctx, [[10, -104], [80, -150]], pal.trunk, 9)
                // the canopy: big overlapping lobes lit from the upper left
                const r = rng(777)
                const lobes = []
                for (let i = 0; i < 14; i++) {
                    const a = (i / 14) * Math.PI * 2
                    lobes.push([Math.cos(a) * s * 1.35, -170 + Math.sin(a) * s * 0.75, s * (0.55 + r() * 0.25)])
                }
                lobes.push([0, -190, s], [-40, -210, s * 0.7], [44, -200, s * 0.7])
                inkedCircles(ctx, lobes, pal.leafDark, ink, 2.2)
                ctx.save(); clipCircles(ctx, lobes)
                lobes.forEach(([x, y, rad]) => { ctx.fillStyle = pal.leaf; ctx.beginPath(); ctx.arc(x - rad * 0.15, y - rad * 0.2, rad * 0.82, 0, Math.PI * 2); ctx.fill() })
                lobes.forEach(([x, y, rad]) => { if (x < 30) { ctx.fillStyle = pal.leafLight; ctx.beginPath(); ctx.arc(x - rad * 0.3, y - rad * 0.4, rad * 0.4, 0, Math.PI * 2); ctx.fill() } })
                for (let i = 0; i < 160; i++) {
                    const x = (r() - 0.5) * s * 3.4, y = -180 + (r() - 0.5) * s * 2
                    ctx.strokeStyle = x < 0 ? mix(pal.leafLight, "#FFFFFF", 0.2) : pal.leafShadow; ctx.globalAlpha = 0.5; ctx.lineWidth = 1.1
                    ctx.beginPath(); ctx.arc(x, y, 2.5 + r() * 2, Math.PI * 0.1, Math.PI * 0.9); ctx.stroke()
                }
                ctx.globalAlpha = 1
                ctx.restore()
                // lanterns hanging from the branches
                lanterns.forEach(([x, y]) => { line(ctx, [[x, y - 14], [x, y]], ink, 1); lantern(ctx, pal, x, y + 8) })
                // the swing
                line(ctx, [[-54, -142], [-60, -48]], ink, 1.3); line(ctx, [[-30, -140], [-34, -48]], ink, 1.3)
                inkRect(ctx, -64, -50, 34, 5, pal.woodLight, ink, { line: 1.1 })
                // plaques on the roots
                ;[[-44, -4, "1812"], [36, -2, "1920"], [0, 8, "2026"]].forEach(([x, y, t]) => {
                    roundRect(ctx, x - 14, y - 8, 28, 12, 3); ctx.fillStyle = pal.woodLight; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1; ctx.stroke()
                    ctx.fillStyle = "#2E3431"; ctx.font = `700 8px ${HAND}`; ctx.textAlign = "center"; ctx.fillText(t, x, y + 1)
                })
            }
        }, bench(l.x - 120, l.y + 36, 56), bench(l.x + 120, l.y + 36, 56)],
        decals: [(ctx, pal) => {
            softShadow(ctx, l.x + 40, baseY + 6, 180, 60, pal.shadowAlpha * 1.2, pal.shadow)
            for (let i = 0; i < 6; i++) paintFlowerClump(ctx, pal, l.x - 140 + i * 56, l.y + 60 + (i % 2) * 8, 551 + i)
        }],
        footprints: [{ circle: true, x: l.x, y: baseY - 4, r: 34 }],
        lights: lanterns.map(([x, y]) => ({ x: l.x + x, y: baseY + y + 10, r: 60 })),
        anchors: {}
    }
}

// ---- south: the party tent ------------------------------------------------------------------

function party(l) {
    const baseY = l.y - 12
    const balloons = [[-108, -96], [-92, -118], [100, -104], [116, -126], [86, -130]]
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-110, -170, 110, 10],
            paint(ctx, pal) {
                const ink = pal.ink
                const red = pal.night ? "#8C3A3A" : "#D65A52", white = pal.cloth
                // side walls in stripes
                for (let i = 0; i < 10; i++) {
                    const x = -90 + i * 18
                    inkPoly(ctx, [[x, 0], [x + 18, 0], [x + 18, -56], [x, -56]], i % 2 ? white : red, ink, { line: 1 })
                }
                // the open door flaps
                inkPoly(ctx, [[-18, 0], [0, -48], [18, 0]], "#3A2A2A", ink, { line: 1.2 })
                inkPoly(ctx, [[-18, 0], [-6, -44], [-28, -56]], red, ink, { line: 1.2 })
                inkPoly(ctx, [[18, 0], [6, -44], [28, -56]], red, ink, { line: 1.2 })
                // the peaked roof, striped too
                for (let i = 0; i < 10; i++) {
                    const x = -96 + i * 19.2
                    inkPoly(ctx, [[x, -54], [x + 19.2, -54], [0, -140]], i % 2 ? red : white, ink, { line: 1 })
                }
                for (let i = 0; i < 10; i++) {
                    ctx.beginPath(); ctx.arc(-86.4 + i * 19.2, -54, 9.6, 0, Math.PI)
                    ctx.fillStyle = i % 2 ? red : white; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1; ctx.stroke()
                }
                line(ctx, [[0, -140], [0, -164]], ink, 1.6)
                inkPoly(ctx, [[0, -164], [22, -158], [0, -150]], pal.flag[1], ink, { line: 1 })
            }
        }, {
            // the cake table
            x: l.x + 70, y: l.y + 30, box: [-30, -60, 30, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                ;[-18, 18].forEach((x) => line(ctx, [[x, 0], [x, -16]], pal.woodDark, 2.4))
                inkEllipse(ctx, 0, -18, 26, 8, pal.cloth, ink, 1.3)
                inkRect(ctx, -12, -36, 24, 16, "#F6C8D8", ink, { line: 1.2 })
                inkRect(ctx, -8, -46, 16, 10, "#FFF2E0", ink, { line: 1 })
                ;[-4, 0, 4].forEach((x) => { line(ctx, [[x, -46], [x, -52]], pal.flag[2], 1.4); ctx.fillStyle = "#F2C14E"; ctx.beginPath(); ctx.arc(x, -54, 1.8, 0, Math.PI * 2); ctx.fill() })
            }
        }],
        decals: [(ctx, pal) => {
            // confetti on the grass
            const r = rng(561)
            for (let i = 0; i < 60; i++) {
                ctx.fillStyle = pal.flag[i % pal.flag.length]
                ctx.globalAlpha = 0.8
                ctx.save(); ctx.translate(l.x + (r() - 0.5) * 240, l.y + 10 + (r() - 0.5) * 70); ctx.rotate(r() * 3)
                ctx.fillRect(-2, -1, 4, 2); ctx.restore()
            }
            ctx.globalAlpha = 1
        }],
        footprints: [{ x: l.x - 92, y: baseY - 40, w: 184, h: 42 }],
        lights: [{ x: l.x, y: baseY - 30, r: 110 }],
        anchors: { balloons: balloons.map(([x, y], i) => ({ x: l.x + x, y: baseY + y, anchorY: baseY + 4, color: i })) }
    }
}

// ---- south: the long table -------------------------------------------------------------------

function dinner(l) {
    const baseY = l.y - 4
    const chairsBack = [-80, -40, 0, 40, 80].map((x) => chair(l.x + x, baseY - 26, 1))
    const chairsFront = [-80, -40, 0, 40, 80].map((x) => chair(l.x + x, baseY + 30, -1))
    return {
        sprites: [...chairsBack, {
            x: l.x, y: baseY + 4, box: [-120, -60, 120, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                ;[-96, 96].forEach((x) => line(ctx, [[x, 0], [x, -20]], pal.woodDark, 3))
                inkRect(ctx, -110, -30, 220, 14, pal.cloth, ink, { line: 1.6 })
                inkPoly(ctx, [[-110, -16], [110, -16], [106, -4], [-106, -4]], pal.clothShade, ink, { line: 1.2 })
                // plates, glasses and candles down the table
                for (let i = 0; i < 6; i++) {
                    const x = -90 + i * 36
                    inkEllipse(ctx, x, -24, 7, 3, "#FFFFFF", ink, 0.9)
                    inkRect(ctx, x + 9, -32, 3, 6, withAlpha(pal.window, 0.9), ink, { line: 0.7 })
                }
                ;[-54, 0, 54].forEach((x) => { inkRect(ctx, x - 2, -40, 4, 12, "#FFF6E0", ink, { line: 0.8 }); ctx.fillStyle = "#F6A93B"; ctx.beginPath(); ctx.ellipse(x, -43, 2, 3.5, 0, 0, Math.PI * 2); ctx.fill() })
                inkEllipse(ctx, -20, -26, 10, 4, "#E8894A", ink, 1)
                inkEllipse(ctx, 26, -26, 9, 4, "#8FB061", ink, 1)
            }
        }, ...chairsFront, ...[-130, 130].map((dx) => ({
            // lantern poles with a string of lights between them
            x: l.x + dx, y: baseY + 4, box: [dx < 0 ? -10 : -270, -110, dx < 0 ? 270 : 10, 6],
            paint(ctx, pal) {
                line(ctx, [[0, 0], [0, -96]], pal.woodDark, 3.2)
                if (dx < 0) {
                    ctx.strokeStyle = pal.ink; ctx.lineWidth = 1
                    ctx.beginPath(); ctx.moveTo(0, -94); ctx.quadraticCurveTo(130, -70, 260, -94); ctx.stroke()
                    for (let i = 1; i < 12; i++) {
                        const t = i / 12, x = 260 * t, y = -94 + 2 * t * (1 - t) * 24
                        ctx.fillStyle = i % 2 ? pal.lamp : pal.flag[1]
                        ctx.beginPath(); ctx.arc(x, y + 4, 3, 0, Math.PI * 2); ctx.fill()
                    }
                }
            }
        }))],
        decals: [(ctx, pal) => {
            ctx.save(); ctx.fillStyle = withAlpha(pal.pathLight, 0.8)
            roundRect(ctx, l.x - 150, l.y - 50, 300, 100, 30); ctx.fill(); ctx.restore()
        }],
        footprints: [{ x: l.x - 112, y: baseY - 24, w: 224, h: 26 }],
        lights: [-100, -50, 0, 50, 100].map((x) => ({ x: l.x + x, y: baseY - 84, r: 60 })),
        anchors: {}
    }
}

// ---- south: the wishing well --------------------------------------------------------------------

function well(l) {
    const baseY = l.y - 4
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-60, -120, 60, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                // stone ring
                inkEllipse(ctx, 0, -30, 38, 14, pal.stoneDark, ink, 1.6)
                inkPoly(ctx, [[-38, -30], [-38, -4], [38, -4], [38, -30]], pal.stone, ink, { line: 1.6 })
                ctx.save(); ctx.beginPath(); ctx.rect(-38, -30, 76, 26); ctx.clip()
                const r = rng(571)
                for (let row = 0; row < 3; row++) for (let i = 0; i < 6; i++) inkRect(ctx, -38 + i * 14 + (row % 2) * 7, -30 + row * 9, 13, 8, r() < 0.5 ? pal.stone : pal.stoneLight, mix(pal.ink, pal.stoneDark, 0.5), { line: 0.7 })
                ctx.restore()
                inkEllipse(ctx, 0, -4, 38, 8, null, ink, 1.4)
                inkEllipse(ctx, 0, -30, 30, 9, pal.waterDeep, ink, 1.3)
                // posts, roof, crank and bucket
                ;[-32, 32].forEach((x) => inkRect(ctx, x - 4, -94, 8, 64, pal.woodDark, ink, { line: 1.3 }))
                inkPoly(ctx, [[-50, -90], [50, -90], [0, -118]], pal.roof, ink, { line: 1.8 })
                line(ctx, [[-32, -76], [32, -76]], pal.wood, 3)
                line(ctx, [[8, -76], [8, -54]], ink, 1)
                inkPoly(ctx, [[1, -54], [15, -54], [13, -42], [3, -42]], pal.wood, ink, { line: 1.1 })
                line(ctx, [[32, -76], [42, -70]], pal.metal, 2)
            }
        }],
        decals: [(ctx, pal) => {
            flagstones(ctx, pal, l.x, l.y + 10, 80, 26, 572)
            paintFlowerClump(ctx, pal, l.x - 60, l.y + 2, 573)
            paintFlowerClump(ctx, pal, l.x + 64, l.y + 4, 574)
        }],
        footprints: [{ circle: true, x: l.x, y: baseY - 12, r: 40 }],
        lights: [],
        anchors: { well: { x: l.x, y: baseY - 30 } }
    }
}

// ---- south: the zen garden ------------------------------------------------------------------------

function zen(l) {
    const pond = { x: l.x + 70, y: l.y + 14, rx: 46, ry: 22 }
    const rocks = [[l.x - 70, l.y - 10, 14], [l.x - 30, l.y + 22, 9], [l.x - 94, l.y + 30, 7]]
    return {
        sprites: [{
            // the torii gate
            x: l.x - 10, y: l.y - 50, box: [-70, -120, 70, 8],
            paint(ctx, pal) {
                const red = pal.night ? "#8C3A3A" : "#C8453A", ink = pal.ink
                ;[-42, 42].forEach((x) => inkRect(ctx, x - 5, -96, 10, 96, red, ink, { line: 1.5 }))
                ctx.beginPath(); ctx.moveTo(-62, -100); ctx.quadraticCurveTo(0, -94, 62, -100); ctx.lineTo(58, -110); ctx.quadraticCurveTo(0, -104, -58, -110); ctx.closePath()
                ctx.fillStyle = "#2B2626"; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.4; ctx.stroke()
                inkRect(ctx, -52, -92, 104, 8, red, ink, { line: 1.3 })
                inkRect(ctx, -5, -92, 10, 18, red, ink, { line: 1 })
            }
        }, {
            // a stone lantern
            x: l.x + 120, y: l.y - 26, box: [-18, -60, 18, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                inkRect(ctx, -10, -8, 20, 8, pal.stone, ink, { line: 1.2 })
                inkRect(ctx, -4, -30, 8, 22, pal.stoneLight, ink, { line: 1.1 })
                inkRect(ctx, -9, -42, 18, 12, pal.stone, ink, { line: 1.2 })
                ctx.fillStyle = pal.night ? pal.lamp : "#FFF1C4"; ctx.fillRect(-4, -39, 8, 6)
                inkPoly(ctx, [[-14, -42], [14, -42], [0, -54]], pal.stoneDark, ink, { line: 1.2 })
            }
        }, ...[[-130, -30], [-150, 10], [150, 30]].map(([dx, dy], i) => ({
            // bamboo clumps
            x: l.x + dx, y: l.y + dy, box: [-24, -110, 24, 6], sway: 0.6,
            paint(ctx, pal) {
                const r = rng(580 + i)
                for (let k = 0; k < 6; k++) {
                    const x = (r() - 0.5) * 28, h = 70 + r() * 36
                    line(ctx, [[x, 0], [x + 2, -h]], pal.ink, 4.2)
                    line(ctx, [[x, 0], [x + 2, -h]], pal.night ? "#4E6A40" : "#9CBF5E", 2.6)
                    for (let y = -14; y > -h; y -= 14) line(ctx, [[x - 2, y], [x + 3, y]], pal.ink, 1)
                    ;[[-10, -h * 0.7], [8, -h * 0.85], [-6, -h]].forEach(([lx, ly]) => {
                        ctx.fillStyle = pal.leaf; ctx.beginPath(); ctx.ellipse(x + lx, ly, 9, 2.4, lx < 0 ? 0.4 : -0.4, 0, Math.PI * 2); ctx.fill()
                    })
                }
            }
        })), ...rocks.map(([x, y, s]) => ({
            x, y, box: [-s * 1.4, -s * 1.3, s * 1.4, 4],
            paint(ctx, pal) {
                ctx.beginPath(); ctx.moveTo(-s, 0); ctx.quadraticCurveTo(-s, -s, 0, -s * 1.1); ctx.quadraticCurveTo(s, -s, s, 0); ctx.closePath()
                ctx.fillStyle = pal.stoneDark; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.4; ctx.stroke()
                ctx.fillStyle = pal.stoneLight; ctx.beginPath(); ctx.ellipse(-s * 0.3, -s * 0.7, s * 0.4, s * 0.2, -0.3, 0, Math.PI * 2); ctx.fill()
                ctx.fillStyle = pal.moss; ctx.beginPath(); ctx.ellipse(s * 0.1, -s * 0.95, s * 0.35, s * 0.12, 0, 0, Math.PI * 2); ctx.fill()
            }
        }))],
        decals: [(ctx, pal) => {
            // raked sand: an oval bed with ripples raked around each rock
            ctx.save()
            ctx.beginPath(); ctx.ellipse(l.x - 30, l.y + 10, 124, 50, 0, 0, Math.PI * 2)
            ctx.fillStyle = pal.night ? "#5A5A50" : "#EDE6D2"; ctx.fill()
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.4; ctx.stroke()
            ctx.clip()
            ctx.strokeStyle = pal.night ? "rgba(0,0,0,0.25)" : "rgba(150,135,100,0.5)"; ctx.lineWidth = 1
            for (let y = l.y - 44; y < l.y + 64; y += 6) { ctx.beginPath(); ctx.moveTo(l.x - 160, y); ctx.bezierCurveTo(l.x - 80, y - 4, l.x, y + 4, l.x + 100, y); ctx.stroke() }
            rocks.forEach(([x, y, s]) => {
                ctx.fillStyle = pal.night ? "#5A5A50" : "#EDE6D2"
                ctx.beginPath(); ctx.ellipse(x, y, s + 20, (s + 20) * 0.5, 0, 0, Math.PI * 2); ctx.fill()
                for (let k = 1; k <= 3; k++) { ctx.beginPath(); ctx.ellipse(x, y, s + k * 6, (s + k * 6) * 0.5, 0, 0, Math.PI * 2); ctx.stroke() }
            })
            ctx.restore()
            // the koi pond (the koi swim in the motion layer)
            ctx.beginPath(); ctx.ellipse(pond.x, pond.y, pond.rx + 6, pond.ry + 4, 0, 0, Math.PI * 2); ctx.fillStyle = pal.stone; ctx.fill()
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
            ctx.beginPath(); ctx.ellipse(pond.x, pond.y, pond.rx, pond.ry, 0, 0, Math.PI * 2)
            const g = ctx.createRadialGradient(pond.x, pond.y, 4, pond.x, pond.y, pond.rx)
            g.addColorStop(0, pal.waterDeep); g.addColorStop(1, pal.water)
            ctx.fillStyle = g; ctx.fill(); ctx.stroke()
        }],
        footprints: [{ x: l.x - 58, y: l.y - 56, w: 10, h: 8 }, { x: l.x + 28, y: l.y - 56, w: 10, h: 8 }, { circle: true, x: pond.x, y: pond.y, r: 30 }, ...rocks.map(([x, y, s]) => ({ circle: true, x, y: y - 2, r: s }))],
        lights: [{ x: l.x + 120, y: l.y - 62, r: 60 }],
        anchors: { koi: pond }
    }
}

// ---- west: the harbour --------------------------------------------------------------------------------

function harbor(l, lake) {
    const pierY = l.y - 4
    const shoreX = lake ? lake.x + lake.rx - 10 : l.x - 100
    const pier = { x: shoreX - 150, y: pierY - 14, w: 160 + (l.x - 70 - shoreX), h: 28 }
    return {
        sprites: [{
            // the boathouse on the shore
            x: l.x + 30, y: l.y - 36, box: [-80, -130, 110, 8],
            paint(ctx, pal) {
                house(ctx, pal, { w: 120, h: 64, depth: 28, roofH: 44, windows: [[-40, -38, 20, 18]], door: false, wall: pal.night ? "#4E5E6A" : "#8FB8CC", wallShade: pal.night ? "#3E4E58" : "#6F98AC" })
                inkRect(ctx, 6, -52, 44, 52, "#3A3230", pal.ink, { line: 1.4 })
                for (let x = 10; x < 50; x += 7) line(ctx, [[x, -50], [x, -2]], "#5A4A44", 1, 0.8)
                ;[[-62, 0], [62, 0]].forEach(([x]) => {})
                // a lifebuoy
                ctx.strokeStyle = "#E85A4A"; ctx.lineWidth = 5
                ctx.beginPath(); ctx.arc(-40, -12, 8, 0, Math.PI * 2); ctx.stroke()
                ctx.strokeStyle = "#FFFFFF"; ctx.lineWidth = 5; ctx.setLineDash([4, 8.5])
                ctx.beginPath(); ctx.arc(-40, -12, 8, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([])
            }
        }, {
            // the departures board
            x: l.x - 40, y: l.y + 40, box: [-50, -86, 50, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                ;[-36, 36].forEach((x) => line(ctx, [[x, 0], [x, -40]], pal.woodDark, 3))
                inkRect(ctx, -46, -84, 92, 48, "#1F2628", ink, { line: 1.8 })
                ctx.fillStyle = "#F2C14E"; ctx.font = `700 8px ${HAND}`; ctx.textAlign = "left"
                ctx.fillText("DEPARTURES", -40, -72)
                ctx.fillStyle = "#EAF5F4"; ctx.font = `600 7px ${HAND}`
                ;["Lisbon   09:40", "Kyoto     11:15", "Oslo       14:05", "Anywhere  soon"].forEach((t, i) => ctx.fillText(t, -40, -62 + i * 7.5))
            }
        }, ...[0, 1].map((i) => ({
            x: pier.x + 30 + i * 60, y: pierY + 16, box: [-6, -26, 6, 4],
            paint(ctx, pal) { line(ctx, [[0, 0], [0, -20]], pal.woodDark, 5); inkEllipse(ctx, 0, -20, 3, 2, pal.woodLight, pal.ink, 1) }
        }))],
        decals: [(ctx, pal) => {
            // the pier over the lake
            softShadow(ctx, pier.x + pier.w / 2 + 6, pier.y + pier.h + 6, pier.w / 2, 12, 0.3, pal.shadow)
            ctx.fillStyle = pal.woodLight; ctx.fillRect(pier.x, pier.y, pier.w, pier.h)
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.6; ctx.strokeRect(pier.x, pier.y, pier.w, pier.h)
            for (let x = pier.x + 8; x < pier.x + pier.w; x += 8) line(ctx, [[x, pier.y], [x, pier.y + pier.h]], pal.woodDark, 1, 0.7)
            // a rope coiled at the end
            ctx.strokeStyle = "#C9A56A"; ctx.lineWidth = 2
            ;[5, 3.5, 2].forEach((rr) => { ctx.beginPath(); ctx.arc(pier.x + 12, pier.y + 14, rr, 0, Math.PI * 2); ctx.stroke() })
            flagstones(ctx, pal, l.x + 20, l.y + 10, 90, 30, 591)
        }],
        footprints: [{ x: l.x - 34, y: l.y - 70, w: 150, h: 36 }, { x: l.x - 80, y: l.y + 30, w: 80, h: 10 }],
        lights: [{ x: l.x - 10, y: l.y - 74, r: 60 }, { x: l.x - 40, y: l.y - 20, r: 50 }],
        walkways: [pier],
        anchors: { boats: lake ? [{ x: pier.x + 40, y: pier.y - 22, a: 0 }, { x: pier.x + 110, y: pier.y + pier.h + 24, a: 1 }, { x: lake.x - 60, y: lake.y + 30, a: 2, drift: true }] : [] }
    }
}

// ---- west: the gallery -----------------------------------------------------------------------------------

function gallery(l) {
    const b = building(l, {
        w: 190, h: 80, depth: 30, roofType: "flat", wall: "#FBF8F0", wallShade: "#E6DFCE",
        windows: [], doorW: 34, doorH: 50, doorColor: "#2F3A40",
        more(ctx, pal) {
            const ink = pal.ink
            // a glass skylight roof
            inkPoly(ctx, [[-80, -88], [80, -88], [60, -118], [-60, -118]], withAlpha(pal.window, 0.85), ink, { line: 1.6 })
            for (let x = -60; x <= 60; x += 20) line(ctx, [[x, -118], [x * 1.33, -88]], ink, 1)
            signBoard(ctx, pal, 0, -60, "GALLERY", pal.cloth)
            // framed paintings on the facade
            ;[[-66, "#6FB3C6", "#F2C14E"], [66, "#E8894A", "#8E7CC3"]].forEach(([x, a, c]) => {
                inkRect(ctx, x - 20, -56, 40, 32, "#C9A04A", ink, { line: 1.6 })
                inkRect(ctx, x - 16, -52, 32, 24, a, ink, { line: 0.8 })
                ctx.fillStyle = c; ctx.beginPath(); ctx.arc(x + 6, -44, 5, 0, Math.PI * 2); ctx.fill()
                ctx.fillStyle = withAlpha(pal.leafDark, 0.9); ctx.beginPath(); ctx.moveTo(x - 16, -28); ctx.lineTo(x - 4, -40); ctx.lineTo(x + 8, -30); ctx.lineTo(x + 16, -36); ctx.lineTo(x + 16, -28); ctx.closePath(); ctx.fill()
            })
        }
    })
    const easel = (x, y, colors, seed) => ({
        x, y, box: [-26, -76, 26, 6],
        paint(ctx, pal) {
            const ink = pal.ink
            line(ctx, [[-14, 0], [0, -64]], pal.woodDark, 2.4); line(ctx, [[14, 0], [0, -64]], pal.woodDark, 2.4); line(ctx, [[0, -64], [4, 0]], pal.woodDark, 2)
            inkRect(ctx, -18, -60, 36, 30, "#FFFFFF", ink, { line: 1.4 })
            const r = rng(seed)
            for (let i = 0; i < 5; i++) { ctx.fillStyle = colors[i % colors.length]; ctx.globalAlpha = 0.85; ctx.beginPath(); ctx.ellipse(-12 + r() * 24, -54 + r() * 20, 4 + r() * 5, 3 + r() * 3, r() * 3, 0, Math.PI * 2); ctx.fill() }
            ctx.globalAlpha = 1
            line(ctx, [[-16, -30], [16, -30]], pal.wood, 2.4)
        }
    })
    return {
        sprites: [b.sprite, easel(l.x - 110, l.y + 30, ["#F39A5B", "#6FB3C6", "#F2C14E"], 601), easel(l.x + 110, l.y + 28, ["#8E7CC3", "#EE9AA8", "#8FB061"], 602), {
            // a little sculpture on a plinth
            x: l.x + 60, y: l.y + 44, box: [-16, -60, 16, 6],
            paint(ctx, pal) {
                inkRect(ctx, -10, -24, 20, 24, pal.stoneLight, pal.ink, { line: 1.3 })
                ctx.beginPath(); ctx.moveTo(-6, -24); ctx.bezierCurveTo(-10, -40, 8, -44, 2, -56); ctx.bezierCurveTo(12, -44, 10, -30, 6, -24); ctx.closePath()
                ctx.fillStyle = "#C9A04A"; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
            }
        }],
        decals: [(ctx, pal) => flagstones(ctx, pal, l.x, l.y + 14, 120, 32, 603)],
        footprints: [b.footprint, { circle: true, x: l.x + 60, y: l.y + 40, r: 10 }],
        lights: [{ x: l.x, y: b.baseY - 100, r: 90 }, { x: l.x - 66, y: b.baseY - 40, r: 50 }, { x: l.x + 66, y: b.baseY - 40, r: 50 }]
    }
}

// ---- west: the portrait studio ------------------------------------------------------------------------------

function persona(l) {
    const b = building(l, {
        w: 150, h: 76, depth: 30, roofType: "gable", roofH: 52, wall: "#F3E3E6", wallShade: "#DEC9CD",
        windows: [], doorW: 26, doorH: 46, doorColor: "#7C6BC4",
        more(ctx, pal) {
            const ink = pal.ink
            // the big north light window artists love
            inkRect(ctx, -64, -66, 42, 46, pal.night ? pal.windowLit : pal.window, ink, { line: 1.6 })
            for (let i = 1; i < 3; i++) { line(ctx, [[-64 + i * 14, -66], [-64 + i * 14, -20]], pal.timber, 1.4); line(ctx, [[-64, -66 + i * 15], [-22, -66 + i * 15]], pal.timber, 1.4) }
            signBoard(ctx, pal, 42, -52, "PORTRAITS", "#F6E7CC")
        }
    })
    return {
        sprites: [b.sprite, {
            // the camera on its tripod
            x: l.x + 104, y: l.y + 30, box: [-20, -64, 20, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                line(ctx, [[-12, 0], [0, -34]], pal.woodDark, 2); line(ctx, [[12, 0], [0, -34]], pal.woodDark, 2); line(ctx, [[0, 0], [0, -34]], pal.woodDark, 2)
                inkRect(ctx, -12, -54, 24, 20, "#3A2E28", ink, { line: 1.4 })
                inkEllipse(ctx, 10, -44, 7, 7, "#6E7477", ink, 1.2)
                inkEllipse(ctx, 10, -44, 3, 3, "#2B3236", null)
                ctx.fillStyle = "#3A2E28"; ctx.beginPath(); ctx.moveTo(-12, -54); ctx.lineTo(-22, -62); ctx.lineTo(-22, -40); ctx.lineTo(-12, -40); ctx.closePath(); ctx.fill()
            }
        }, {
            // the sitter's chair, with a portrait on an easel beside it
            x: l.x - 100, y: l.y + 32, box: [-40, -72, 30, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                inkRect(ctx, -10, -18, 22, 6, "#8E4A5A", ink, { line: 1.2 })
                line(ctx, [[-8, 0], [-8, -12]], pal.woodDark, 2.2); line(ctx, [[10, 0], [10, -12]], pal.woodDark, 2.2)
                inkRect(ctx, -10, -38, 4, 22, "#8E4A5A", ink, { line: 1 })
                line(ctx, [[-34, 0], [-26, -60]], pal.woodDark, 2); line(ctx, [[-18, 0], [-26, -60]], pal.woodDark, 2)
                inkRect(ctx, -38, -60, 24, 28, "#FFFBF0", ink, { line: 1.2 })
                inkEllipse(ctx, -26, -50, 5, 6, "#E8C4A8", ink, 0.8)
                ctx.fillStyle = "#7C6BC4"; ctx.beginPath(); ctx.ellipse(-26, -38, 8, 5, 0, Math.PI, 0); ctx.fill()
            }
        }],
        decals: [(ctx, pal) => flagstones(ctx, pal, l.x, l.y + 14, 110, 30, 611)],
        footprints: [b.footprint],
        lights: [{ x: l.x - 43, y: b.baseY - 44, r: 70 }]
    }
}

// ---- west: the post office ------------------------------------------------------------------------------------

function postoffice(l) {
    const b = building(l, {
        w: 170, h: 78, depth: 30, roofType: "hip", roofH: 40, roof: "#B8433A", bricks: true, wall: "#F0DCC8", wallShade: "#DCC4AE",
        windows: [[-54, -42, 26, 24], [54, -42, 26, 24]], doorW: 28, doorH: 48, doorColor: "#2E5A8C",
        more(ctx, pal) { signBoard(ctx, pal, 0, -64, "POST OFFICE", "#F6E7CC") }
    })
    return {
        sprites: [b.sprite, {
            // the red pillar box
            x: l.x + 110, y: l.y + 26, box: [-14, -50, 14, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                inkRect(ctx, -10, -36, 20, 36, "#C8342E", ink, { line: 1.4 })
                ctx.beginPath(); ctx.moveTo(-11, -36); ctx.quadraticCurveTo(0, -48, 11, -36); ctx.closePath(); ctx.fillStyle = "#C8342E"; ctx.fill(); ctx.stroke()
                inkRect(ctx, -6, -28, 12, 3, "#2B1E1C", ink, { line: 0.6 })
                inkRect(ctx, -12, -4, 24, 4, "#8A2420", ink, { line: 1 })
            }
        }, {
            // parcels stacked by the door
            x: l.x - 108, y: l.y + 28, box: [-26, -40, 26, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                ;[[-16, 0, 20, 16], [4, 0, 18, 14], [-10, -16, 16, 12]].forEach(([x, y, w, h]) => {
                    inkRect(ctx, x, y - h, w, h, "#C9A56A", ink, { line: 1.1 })
                    line(ctx, [[x + w / 2, y - h], [x + w / 2, y]], "#8A6A3A", 1.4)
                    line(ctx, [[x, y - h / 2], [x + w, y - h / 2]], "#8A6A3A", 1.4)
                })
            }
        }],
        decals: [(ctx, pal) => cobbles(ctx, pal, l.x, l.y + 16, 110, 30, 621)],
        footprints: [b.footprint, { circle: true, x: l.x + 110, y: l.y + 22, r: 11 }],
        lights: b.lights
    }
}
